import { FileEntry } from '@st/util/archive/file-entry'
import type { FileReadError, InvalidArchiveError } from '@st/util/archive/archive-errors'
import { unzipEntries } from '@st/util/archive/zip-entries'
import { unpackUpload, type UploadSelection } from '@st/util/archive/unpack-upload'
import { isJunkFilename } from '@st/util/file'
import { Effect } from 'effect'
import { UploadContainer } from './upload-container'

/**
 * Detect the type of upload from a diverse set of possible sources and return a container
 * that can be resolved to a standard list of FileEntry objects.
 *
 * A file or set of files can be uploaded in multiple ways:
 * - Dragging a list of files on the browser window
 * - Dragging a folder on the browser window
 * - Dragging a zip file on the browser window
 *
 * While handling each one has a very different implementation, we want a standard way to work with them.
 */
export type CapturedUploadKind =
  | Extract<UploadContainer, { source: 'file_list' | 'zip' }>
  | { source: 'folder' }

export function classifyUploadSelection(upload: UploadSelection): CapturedUploadKind {
  const files = upload.items.flatMap((item) => (item.type == 'file' ? [item.entry] : []))

  if (upload.items.some((item) => item.type == 'directory')) {
    return { source: 'folder' }
  }

  if (files.length == 1 && files[0].file.name.endsWith('.zip')) {
    return { source: 'zip', zip: files[0] }
  }

  return { source: 'file_list', files }
}

export function detectUploadContainer(
  upload: UploadSelection
): Effect.Effect<UploadContainer, FileReadError> {
  const kind = classifyUploadSelection(upload)
  if (kind.source == 'folder') {
    return unpackUpload(upload).pipe(
      Effect.map((entries) => ({ source: 'folder', fileEntries: entries }) as const)
    )
  }

  return Effect.succeed(kind)
}

export function resolveFilesFromUploadContainer(
  uploadContainer: UploadContainer
): Effect.Effect<FileEntry[], FileReadError | InvalidArchiveError> {
  switch (uploadContainer.source) {
    case 'file_list':
      return Effect.succeed(
        uploadContainer.files.filter((entry) => !isJunkFilename(entry.path))
      )
    case 'zip':
      return uploadContainer.zip.readBytes.pipe(
        Effect.flatMap(unzipEntries),
        Effect.map((entries) => entries.filter((entry) => !isJunkFilename(entry.path)))
      )
    case 'folder':
      return Effect.succeed(
        uploadContainer.fileEntries.filter((entry) => !isJunkFilename(entry.path))
      )
  }
}
